import { Download } from '@mui/icons-material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  List,
  ListItem,
  Pagination,
  Typography,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLowercaseRest, onDownload } from 'src/common/utils/helpers';
import { Commission } from 'src/components/commissions';
import { _Locale } from 'src/components/commissions/BasicTable';
import { Speciality } from 'src/components/specialities';
import { dateFormat } from 'src/hooks';
import { ApiApplication } from 'src/http';
import { Region } from '../applications/applications-user';
import { Application, User } from '../applications/applications-user/BasicTable';
import useStyles from './styles';

interface ApplicationHistory {
  id: number;
  message: string;
  additional: string | null;
  status: string;
  createdAt: string;
  user: User;
}

interface Invoice {
  bank_account: string;
  billing_service_id: number;
  budget_account: string;
  paid_date: string;
  paid_sum: string;
  remain_sum: string;
  reused: boolean;
  serial: string;
  service_name: string;
  service_name_oz: string;
  service_name_ru: string;
  service_name_uz: string;
  status: string;
  sum: string;
  updated_date: string;
}

export default function CheckingApplicationsTable({
  data,
  commissions,
  specialities,
  regions,
  changePagination,
  total,
}: {
  data: Application[] | null;
  commissions: Commission[] | null;
  specialities: Speciality[] | null;
  regions: Region[] | null;
  changePagination: Function;
  total: number;
}) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();

  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange =
    (panel: string, pinfl?: string) => async (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
      setHistory(null);
      setCurrentWork(null);
      setWorkHistory(null);
      setDiplomaHistory(null);
      setDiplomaMessage(null);
      if (isExpanded) {
        await getHistory(panel);
        await checkDiplomas(pinfl || '');
        setLoadingCurrentWork(true);
        setLoadingWorkHistory(true);
        await getCurrentWork(pinfl || '');
        await getWorkHistory(pinfl || '');
      }
    };

  const pageChangeHandler = (event: any, pageNumber: string | number) => {
    changePagination(pageNumber);
  };

  // Invoyslar
  const [invoices, setInvoices] = useState<Invoice[] | null>(null);
  const [loadingInvoice, setLoadingInvoice] = useState<boolean>(false);
  const getInvoice = async (id: string) => {
    setLoadingInvoice(true);
    try {
      const data = await ApiApplication.getMyGovInvoice(id);
      setInvoices(data);
    } catch ({ message }: any) {
      console.log(message);
    } finally {
      setLoadingInvoice(false);
    }
  };

  // O'zgarishlar tarixi
  const [history, setHistory] = useState<ApplicationHistory[] | null>(null);
  const getHistory = async (id: string) => {
    try {
      const data = await ApiApplication.getHistory(id);
      setHistory(data.hits);
    } catch ({ message }: any) {
      console.log(message);
    }
  };

  // Hozirgi ish joyi
  const [loadingCurrentWork, setLoadingCurrentWork] = useState<boolean>(false);
  const [currentWork, setCurrentWork] = useState<any>(null);
  const getCurrentWork = async (id: string) => {
    try {
      const data = await ApiApplication.getCurrentWork(id);
      setCurrentWork(data?.data[0]);
    } catch ({ message }: any) {
      console.log(message);
    } finally {
      setLoadingCurrentWork(false);
    }
  };

  // Ish faoliyati tarixi
  const [loadingWorkHistory, setLoadingWorkHistory] = useState<boolean>(false);
  const [workHistory, setWorkHistory] = useState<any>(null);
  const getWorkHistory = async (id: string) => {
    try {
      const data = await ApiApplication.getWorkHistory(id);
      setWorkHistory(data.data);
    } catch ({ message }: any) {
      console.log(message);
    } finally {
      setLoadingWorkHistory(false);
    }
  };

  // Diplomlar tarixi
  const [loadingDiplomaHistory, setLoadingDiplomaHistory] = useState<boolean>(false);
  const [diplomaHistory, setDiplomaHistory] = useState<any>(null);
  const [diplomaMessage, setDiplomaMessage] = useState<string | null>(null);
  const checkDiplomas = async (pinfl: string | number) => {
    setLoadingDiplomaHistory(true);
    try {
      const data = await ApiApplication.checkDiplomas(pinfl);
      if (data.status === 404) {
        setDiplomaMessage(data.data);
      } else {
        setDiplomaHistory(data.data);
      }
    } catch ({ message }: any) {
      console.log(message);
    } finally {
      setLoadingDiplomaHistory(false);
    }
  };

  return (
    <>
      <Box>
        {data?.length ? (
          <Grid container spacing={2} mt={2}>
            <Grid item xs={1}>
              <Typography fontSize={18} ml={2} display={'flex'} fontWeight={'bold'}>
                №
              </Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography fontSize={18} ml={2} display={'flex'} fontWeight={'bold'}></Typography>
            </Grid>
            <Grid item xs={2}>
              <Typography fontSize={18} ml={2} display={'flex'} fontWeight={'bold'}>
                {t('date')}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography fontSize={18} ml={2} display={'flex'} fontWeight={'bold'}>
                {t('name.full')}
              </Typography>
            </Grid>
            <Grid item xs={3}></Grid>
          </Grid>
        ) : null}
        <Box mt={1}>
          {data?.map((d) => (
            <Accordion
              key={d.id}
              expanded={expanded === d.id?.toString()}
              onChange={handleChange(d.id ? d.id.toString() : '', d.user?.pin || '')}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel4bh-content"
                id="panel4bh-header"
              >
                <Grid container spacing={2}>
                  <Grid item xs={1}>
                    <Box display={'flex'}>
                      <Typography fontSize={16} width={70} mr={2}>
                        {d.id}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={2}>
                    {d.appFrom === 'mygov.uz' ? (
                      <img src="/assets/my.gov.uz.svg" alt="" width={150} />
                    ) : null}
                  </Grid>
                  <Grid item xs={2}>
                    <Typography fontSize={16} ml={2} display={'flex'} alignItems={'center'}>
                      {dateFormat(d?.createdAt || new Date(), 'D.M.Y T')}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography
                      fontSize={18}
                      fontWeight={'bold'}
                      ml={2}
                      display={'flex'}
                      alignItems={'center'}
                    >
                      {capitalizeFirstLowercaseRest(d?.user?.lastName) +
                        ' ' +
                        capitalizeFirstLowercaseRest(d?.user?.firstName) +
                        ' ' +
                        capitalizeFirstLowercaseRest(d?.user?.middleName)}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography>
                      {d.appType === '1' ? (
                        <span>{t('Врачлар ва фармацевтлар')}</span>
                      ) : d.appType === '2' ? (
                        <span>
                          {t(
                            '“Олий ҳамширалик иши” мутахассислигига эга бўlgan hamshiralar, o‘rta tibbiyot va o‘rta farmatsevtika xodimlari'
                          )}
                        </span>
                      ) : (
                        <span>{t('Бирламчи тиббиёт ходимлари')}</span>
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                <Box display={'flex'} justifyContent={'space-between'} alignItems={'flex-start'}>
                  <Box sx={{ width: '48%' }}>
                    <List className={classes.list} sx={{ p: 0 }}>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('name.full', 'F.I.Sh')}
                        </Typography>
                        <Typography variant="subtitle2">
                          {capitalizeFirstLowercaseRest(d?.user?.lastName) +
                            ' ' +
                            capitalizeFirstLowercaseRest(d?.user?.firstName) +
                            ' ' +
                            capitalizeFirstLowercaseRest(d?.user?.middleName)}
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('Ariza raqami')}:
                        </Typography>
                        <Typography variant="subtitle2"> {d.uid}</Typography>
                      </ListItem>
                      {d.appFrom === 'mygov.uz' ? (
                        <ListItem className={classes.listItem}>
                          <Typography variant="body2" mr={3} color="grey.600">
                            {t('My.gov.uz raqami')}:
                          </Typography>
                          <Typography variant="subtitle2"> {d.taskId}</Typography>
                        </ListItem>
                      ) : null}
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('status', 'Status')}:
                        </Typography>
                        <Typography variant="subtitle2">
                          {' '}
                          {t('appealStatus.' + d.status)}
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="h3" my={1} color="grey.900">
                          {t("Shaxsiy ma'lumotlar", "Shaxsiy ma'lumotlar")}
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('profile.phone', 'Telefon')}:
                        </Typography>
                        <Typography variant="subtitle2">+998 {d.indPhone}</Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('profile.dob', "Tug'ilgan sanasi")}:
                        </Typography>
                        <Typography variant="subtitle2">{d?.user?.dob}</Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('profile.pin', 'PINFL')}:
                        </Typography>
                        <Typography variant="subtitle2">{d?.user?.pin}</Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('profile.passportSerialNumber', 'passportSerialNumber')}:
                        </Typography>
                        <Typography variant="subtitle2">
                          {d?.user?.passportSeries + ' ' + d?.user?.passportNumber}
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('profile.passportIssueDate', 'passportIssueDate')}:
                        </Typography>
                        <Typography variant="subtitle2">{d?.user?.passportIssueDate}</Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('profile.passportIssuePlace', 'passportIssuePlace')}:
                        </Typography>
                        <Typography variant="subtitle2">{d?.user?.passportIssuePlace}</Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('profile.passportExpireDate', 'passportExpireDate')}:
                        </Typography>
                        <Typography variant="subtitle2">{d?.user?.passportExpireDate}</Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('profile.gender', 'gender')}:
                        </Typography>
                        <Typography variant="subtitle2">
                          {t('gender.' + d?.user?.gender)}
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          OneID {t('profile.phone', 'phone')}:
                        </Typography>
                        <Typography variant="subtitle2">{d?.user?.phone}</Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('profile.email', 'email')}:
                        </Typography>
                        <Typography variant="subtitle2">{d?.user?.email}</Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="h3" my={1} color="grey.900">
                          I. {t('Малакасига оид маълумотlar', "Malakasiga oid ma'lumotlar")}
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('workplace', 'workplace')}:
                        </Typography>
                        <Typography variant="subtitle2">{d?.workplace}</Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('position', 'position')}:
                        </Typography>
                        <Typography variant="subtitle2">{d?.position}</Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('specialty', 'Mutaxassislik')}:
                        </Typography>
                        <Typography variant="subtitle2">
                          {
                            specialities?.filter((c) => c.id === d.specializationId)[0]?.title[
                              i18n.language as _Locale
                            ]
                          }
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600">
                          {t('workExperienceYear', 'workExperienceYear')}:
                        </Typography>
                        <Typography variant="subtitle2">{d?.workExperienceYear}</Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600" maxWidth={'50%'}>
                          {t('specialtyWorkExperience', 'specialtyWorkExperience')}:
                        </Typography>
                        <Typography variant="subtitle2">{d?.specialtyWorkExperience}</Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600" maxWidth={'50%'}>
                          {t('claimedQualification', 'claimedQualification')}:
                        </Typography>
                        <Typography variant="subtitle2">
                          {t(
                            ['Олий', 'Биринчи', 'Иккинчи', 'Учинчи'][
                              Number(d.claimedQualification) - 1
                            ]
                          )}
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.listItem} alignItems="flex-start">
                        <Typography variant="body2" mr={3} color="grey.600" maxWidth={'50%'}>
                          {t('electronicWorkBook', 'electronicWorkBook')}:
                        </Typography>
                        {d.electronicWorkBook ? (
                          <Button
                            onClick={() => onDownload(d.electronicWorkBook, d.appFrom)}
                            variant="outlined"
                            color="primary"
                            sx={{ p: '5px 10px' }}
                          >
                            <Download /> &nbsp;
                            {t('action.download')}
                          </Button>
                        ) : null}
                      </ListItem>
                      <ListItem className={classes.listItem} alignItems="flex-start">
                        <Typography variant="body2" mr={3} color="grey.600" maxWidth={'50%'}>
                          {t('workplace', 'Hozirgi ish joyi')}:
                        </Typography>
                        {loadingCurrentWork ? (
                          <Box display={'flex'} justifyContent={'center'} width={'100%'}>
                            <CircularProgress sx={{ my: 2 }} />
                          </Box>
                        ) : (
                          <Typography variant="subtitle2">
                            {currentWork?.org}
                          </Typography>
                        )}
                      </ListItem>
                      <ListItem className={classes.listItem} alignItems="flex-start">
                        <Typography variant="h3" my={1} color="grey.900">
                          II.{' '}
                          {t('Профессионал таълим ташкилотларида ўқиганлигини тасдиқловчи архив')}
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.listItem} alignItems="flex-start">
                        <Typography variant="body2" mr={3} color="grey.600" maxWidth={'50%'}>
                          {t('copyDiploma', 'Diplom nusxasi')}:
                        </Typography>
                        {d.copyDiploma ? (
                          <Button
                            onClick={() => onDownload(d.copyDiploma, d.appFrom)}
                            variant="outlined"
                            color="primary"
                            sx={{ p: '5px 10px' }}
                          >
                            <Download /> &nbsp;
                            {t('action.download')}
                          </Button>
                        ) : null}
                      </ListItem>
                      <ListItem className={classes.listItem} alignItems="flex-start">
                        <Typography variant="body2" mr={3} color="grey.600" maxWidth={'50%'}>
                          {t(
                            'copyDocTraining',
                            'Xorijiy davlatlarda olingan hujjatlarning nusxasi (mavjud bo‘lsa: ilmiy seminarlar, syezdlar, konferensiyalarda qatnashganligi hamda malaka oshirganligini tasdiqlovchi diplom, guvohnoma, sertifikat va boshqalar)'
                          )}
                          :
                        </Typography>
                        {d.copyDocTraining ? (
                          <Button
                            onClick={() => onDownload(d.copyDocTraining, d.appFrom)}
                            variant="outlined"
                            color="primary"
                            sx={{ p: '5px 10px' }}
                          >
                            <Download /> &nbsp;
                            {t('action.download')}
                          </Button>
                        ) : null}
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="h3" my={1} color="grey.900">
                          III. {t('Амалдаги малака тоифаси: (агар мавжуд бўлса)')}
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600" maxWidth={'50%'}>
                          {t('qualificationCategory', 'qualificationCategory')}:
                        </Typography>
                        {d.appFrom === 'mygov.uz' ? (
                          <Typography variant="subtitle2">
                            {d.qualificationCategory === ''
                              ? t('attestation.absent')
                              : t(
                                  ['attestation.absent', 'Олий', 'Биринчи', 'Иккинчи', 'Учинчи'][
                                    Number(d.qualificationCategory) - 1
                                  ]
                                )}
                          </Typography>
                        ) : (
                          <Typography variant="subtitle2">
                            {t(
                              ['attestation.absent', 'Олий', 'Биринчи', 'Иккинчи', 'Учинчи'][
                                Number(d.qualificationCategory)
                              ]
                            )}
                          </Typography>
                        )}
                      </ListItem>
                      {d.qualificationCategory !== '0' ? (
                        <>
                          <ListItem className={classes.listItem}>
                            <Typography variant="body2" mr={3} color="grey.600">
                              {t('qualificationCategoryIssued', 'Berilgan sanasi')}:
                            </Typography>
                            <Typography variant="subtitle2">
                              {d?.qualificationCategoryIssued}
                            </Typography>
                          </ListItem>
                          <ListItem className={classes.listItem}>
                            <Typography variant="body2" mr={3} color="grey.600">
                              {t('qualificationCategorySpecialty', 'Berilgan vaqti')}:
                            </Typography>
                            <Typography variant="subtitle2">
                              {d?.qualificationCategorySpecialty}
                            </Typography>
                          </ListItem>
                          <ListItem className={classes.listItem} alignItems="flex-start">
                            <Typography variant="body2" mr={3} color="grey.600" maxWidth={'50%'}>
                              {t('copyQualificationCategory', 'Sertifikat nusxasi')}:
                            </Typography>
                            {d.copyQualificationCategory ? (
                              <Button
                                onClick={() => onDownload(d.copyQualificationCategory, d.appFrom)}
                                variant="outlined"
                                color="primary"
                                sx={{ p: '5px 10px' }}
                              >
                                <Download /> &nbsp;
                                {t('action.download')}
                              </Button>
                            ) : null}
                          </ListItem>
                        </>
                      ) : null}
                      <ListItem className={classes.listItem}>
                        <Typography variant="h3" my={1} color="grey.900">
                          III. {t('Кейинги 5 йил давомида малака оширганлиги')}
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600" maxWidth={'50%'}>
                          {t('creditAccumulated', 'Kreditlar hajmi')}:
                        </Typography>
                        <Typography variant="subtitle2">{d.creditAccumulated}</Typography>
                      </ListItem>
                      <ListItem className={classes.listItem} alignItems="flex-start">
                        <Typography variant="body2" mr={3} color="grey.600" maxWidth={'50%'}>
                          {t('copyCertificateQualification', 'Sertigikat nusxasi')}:
                        </Typography>
                        {d.copyCertificateQualification ? (
                          <Button
                            onClick={() => onDownload(d.copyCertificateQualification, d.appFrom)}
                            variant="outlined"
                            color="primary"
                            sx={{ p: '5px 10px' }}
                          >
                            <Download /> &nbsp;
                            {t('action.download')}
                          </Button>
                        ) : null}
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600" maxWidth={'50%'}>
                          {t('commission', 'Komissiya')}:
                        </Typography>
                        <Typography variant="subtitle2">
                          {
                            commissions?.filter((c) => c.id === d.commissionId)[0]?.title[
                              i18n.language as _Locale
                            ]
                          }
                        </Typography>
                      </ListItem>
                      <ListItem className={classes.listItem}>
                        <Typography variant="body2" mr={3} color="grey.600" maxWidth={'50%'}>
                          {t('region', 'Hudud')}:
                        </Typography>
                        <Typography variant="subtitle2">
                          {
                            regions?.filter((c) => c.id === d.regionId)[0]?.title[
                              i18n.language as _Locale
                            ]
                          }
                        </Typography>
                      </ListItem>
                    </List>
                  </Box>
                  <Box sx={{ width: '48%' }}>
                    {/* DIPLOMA HISTORY */}
                    <List className={classes.list} sx={{ p: 0 }}>
                      <ListItem
                        className={classes.listItem}
                        sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 3 }}
                      >
                        <Typography variant="h5" my={1} color="grey.900" marginY={0}>
                          {t('copyDiploma', 'Diplom tarixi')}
                        </Typography>
                      </ListItem>
                    </List>
                    {loadingDiplomaHistory ? (
                      <Box display={'flex'} justifyContent={'center'} width={'100%'}>
                        <CircularProgress sx={{ my: 2 }} />
                      </Box>
                    ) : (
                      <>
                        {diplomaHistory ? (
                          <>
                            {diplomaHistory?.length ? (
                              <TableContainer component={Paper}>
                                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>{t("Ta'lim")}</TableCell>
                                      <TableCell align="right">{t('Tashkilot')}</TableCell>
                                      <TableCell align="right">{t('speciality')}</TableCell>
                                      <TableCell align="right">{t('Boshi')}</TableCell>
                                      <TableCell align="right">{t('Oxiri')}</TableCell>
                                      <TableCell align="right">{t('Turi')}</TableCell>
                                      <TableCell align="right">{t('Universitet')}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {diplomaHistory?.map((row: any, index: number) => (
                                      <TableRow
                                        key={index}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                      >
                                        <TableCell component="th" scope="row">
                                          {row.degree_name}
                                        </TableCell>
                                        <TableCell align="right">
                                          {row.institution_type_name}
                                        </TableCell>
                                        <TableCell align="right">{row.speciality_name}</TableCell>
                                        <TableCell align="right">{row.edu_starting_date}</TableCell>
                                        <TableCell align="right">
                                          {row.edu_finishing_date}
                                        </TableCell>
                                        <TableCell align="right">{row.edu_form_name}</TableCell>
                                        <TableCell align="right">{row.institution_name}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Alert severity="info" sx={{ mt: 2 }}>
                                Ma'lumot mavjud emas
                              </Alert>
                            )}
                          </>
                        ) : null}
                      </>
                    )}
                    {diplomaMessage && (
                      <Box p={3}>
                        <Typography variant="body1">{diplomaMessage}!</Typography>
                      </Box>
                    )}
                    <br />
                    {/* WORK HISTORY */}
                    <List className={classes.list} sx={{ p: 0 }}>
                      <ListItem
                        className={classes.listItem}
                        sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                      >
                        <Typography variant="h3" my={1} color="grey.900">
                          {t('jobs.historyBook', 'Mehnat daftarchasi')}
                        </Typography>{' '}
                        {d.documentWork && (
                          <a
                            href={d.documentWork}
                            style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <svg
                              className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-i4bv87-MuiSvgIcon-root"
                              focusable="false"
                              aria-hidden="true"
                              viewBox="0 0 24 24"
                              data-testid="DownloadRoundedIcon"
                            >
                              <path
                                fill="rgb(102, 126, 234)"
                                d="M16.59 9H15V4c0-.55-.45-1-1-1h-4c-.55 0-1 .45-1 1v5H7.41c-.89 0-1.34 1.08-.71 1.71l4.59 4.59c.39.39 1.02.39 1.41 0l4.59-4.59c.63-.63.19-1.71-.7-1.71zM5 19c0 .55.45 1 1 1h12c.55 0 1-.45 1-1s-.45-1-1-1H6c-.55 0-1 .45-1 1z"
                              ></path>
                            </svg>
                            {t('action.download', 'Yuklab olish')}
                          </a>
                        )}
                      </ListItem>
                    </List>
                    {loadingWorkHistory ? (
                      <Box display={'flex'} justifyContent={'center'} width={'100%'}>
                        <CircularProgress sx={{ my: 2 }} />
                      </Box>
                    ) : (
                      <>
                        {workHistory ? (
                          <>
                            {workHistory.length ? (
                              <TableContainer component={Paper}>
                                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>{t('jobs.companyName')}</TableCell>
                                      <TableCell align="right">{t('jobs.companyName')}</TableCell>
                                      <TableCell align="right">{t("Bo'limi")}</TableCell>
                                      <TableCell align="right">{t('jobs.startDate')}</TableCell>
                                      <TableCell align="right">{t('jobs.endDate')}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {workHistory?.map((row: any, index: number) => (
                                      <TableRow
                                        key={index}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                      >
                                        <TableCell component="th" scope="row">
                                          {row.company_name}
                                        </TableCell>
                                        <TableCell align="right">{row.structure_name}</TableCell>
                                        <TableCell align="right">{row.position_name}</TableCell>
                                        <TableCell align="right">{row.start_date}</TableCell>
                                        <TableCell align="right">
                                          {row.end_date ? row.end_date : t('jobs.untilNow')}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            ) : (
                              <Alert severity="error" sx={{ mt: 2 }}>
                                Ma'lumot mavjud emas
                              </Alert>
                            )}
                          </>
                        ) : null}
                      </>
                    )}
                    {history?.length ? (
                      <List className={classes.list} sx={{ p: 0 }}>
                        <ListItem className={classes.listItem}>
                          <Typography variant="h3" my={1} color="grey.900">
                            {t('history.changes', "O'zgarishlar tarixi")}
                          </Typography>
                        </ListItem>
                        {history.map((h, idx) => (
                          <ListItem className={classes.listItem} key={idx}>
                            <Typography variant="body2" mr={3} color="grey.600" maxWidth={'50%'}>
                              {t('appealStatus.' + h.status)} <br />{' '}
                              <b> {h?.user?.firstName + ' ' + h?.user?.lastName}</b>
                              <br /> <b> {dateFormat(h.createdAt, 'D.M.Y T')}</b>
                            </Typography>
                            {h.message && h.message !== h.status ? (
                              <Typography variant="subtitle2">{h.message}</Typography>
                            ) : h.message === h.status ? (
                              <Typography variant="subtitle2"> - </Typography>
                            ) : null}
                          </ListItem>
                        ))}
                      </List>
                    ) : null}
                    {d.invoice ? (
                      <Box display={'flex'} mt={4}>
                        <Button
                          onClick={() => onDownload(d?.invoice, d.appFrom)}
                          variant="outlined"
                          color="primary"
                          sx={{ p: '5px 10px' }}
                        >
                          <Download /> &nbsp;
                          {t('Invoice')}
                        </Button>
                      </Box>
                    ) : null}
                    {/* MyGov Invoice */}
                    <List className={classes.list} sx={{ p: 0 }}>
                      <ListItem
                        className={classes.listItem}
                        sx={{ justifyContent: 'space-between' }}
                      >
                        <Typography variant="h3" my={1} color="grey.900">
                          {t('MyGovInvoice', 'MyGovdan invoysni yuklab olish')}
                        </Typography>
                        <Button variant="outlined" onClick={() => getInvoice(d.taskId || '')}>
                          <DownloadRoundedIcon />
                          {t('action.download')}
                        </Button>
                      </ListItem>
                    </List>
                    {loadingInvoice ? (
                      <Box display={'flex'} justifyContent={'center'} width={'100%'}>
                        <CircularProgress sx={{ my: 2 }} />
                      </Box>
                    ) : (
                      <>
                        {invoices ? (
                          <>
                            {invoices.length ? (
                              <>
                                <List className={classes.list} sx={{ p: 0 }}>
                                  <ListItem
                                    className={classes.listItem}
                                    sx={{ justifyContent: 'space-between' }}
                                  >
                                    <Typography variant="h5" my={1} color="grey.900">
                                      {invoices[0]?.service_name}
                                    </Typography>
                                  </ListItem>
                                </List>
                                <TableContainer component={Paper}>
                                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell align="right">
                                          {t("Pul to'langan vaqti")}
                                        </TableCell>
                                        <TableCell align="right">
                                          {t("Pul to'langan summasi")}
                                        </TableCell>
                                        <TableCell align="right">{t("Qoldig'i")}</TableCell>
                                        <TableCell align="right">{t('status')}</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {invoices?.map((row: any, index: number) => (
                                        <TableRow
                                          key={index}
                                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                          <TableCell align="right">{row.paid_date}</TableCell>
                                          <TableCell align="right">{row.paid_sum}</TableCell>
                                          <TableCell align="right">{row.remain_sum}</TableCell>
                                          <TableCell align="right">{row.status}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </>
                            ) : (
                              <Alert severity="error" sx={{ mt: 2 }}>
                                Ma'lumot mavjud emas
                              </Alert>
                            )}
                          </>
                        ) : null}
                      </>
                    )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
      {data?.length ? (
        <Box display={'flex'} justifyContent={'center'} my={3}>
          <Pagination
            count={total}
            variant="outlined"
            shape="rounded"
            onChange={(event, pageNumber) => pageChangeHandler(event, pageNumber)}
          />
        </Box>
      ) : null}
    </>
  );
}
